import React, { useCallback } from 'react'
import { Grid, TextareaAutosize } from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import PageLayout from '../../common/PageLayout'
import PageHeading from '../../common/PageHeading'
import Divider from '../../../../../components/atoms/Divider'
import SydLabel, { hookFormErrorAdapter } from '../../../../../components/commonDesign/SydLabel'
import SydSelect from '../../../../../components/commonDesign/SydSelect'
import FlexRow from '../../../../../components/molecules/FlexRow'
import SydButton from '../../../../../components/commonDesign/Button'
import SydInput from '../../../../../components/commonDesign/SydInput'
import { useCreateAiScheduledPrompt } from '../../../../../api/denaliAssistant'
import { boundaryOptions, intentOptions, intervalOptions } from '../options'

const useSubmitter = (form, onComplete) => {
  const { handleSubmit, clearErrors } = form
  const { mutateAsync: createScheduledPrompt, isLoading } = useCreateAiScheduledPrompt()
  const onSubmit = useCallback(async (formData) => {
    const command = {
      name: formData.name || null,
      prompt: formData.prompt || null,
      intent: formData.intent || null,
      schedule: formData.schedule || null
    }

    try {
      form?.clearErrors?.()
      const result = await createScheduledPrompt(command)
      onComplete(result)
    } catch (err) {
      form.setError('root', {
        type: 'custom',
        message: err.message || 'Something unexpected happened while creating the scheduled prompt.'
      })
    }
  }, [form, createScheduledPrompt, onComplete])

  const submitter = useCallback(async (e) => {
    const onValid = async (form) => {
      await onSubmit(form)
    }
    const onInvalid = (errors) => {
      console.error(errors)
    }
    clearErrors()
    const handler = handleSubmit(onValid, onInvalid)
    await handler(e)
  }, [handleSubmit, clearErrors, onSubmit])

  return {
    submitter,
    processing: isLoading
  }
}

function NewSchedule () {
  const history = useHistory()
  const onComplete = useCallback((result) => {
    console.log(result, 'result')
    history.push(`/denali-ai/schedules/${result?.scheduledPrompt?.scheduleId}`)
  }, [history])
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      name: '',
      intent: 'data_inquiry',
      prompt: '',
      schedule: {
        interval: 'week',
        bound: 'end'
      }
    }
  })
  const { submitter, processing } = useSubmitter(form, onComplete)

  return (
    <PageLayout variant='constrained'>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <PageHeading title='New Schedule' />
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name='name'
            control={form.control}
            rules={{
              maxLength: { value: 64, message: 'Maximum 64 characters' }
            }}
            render={({ field }) => (
              <SydLabel label='Name'>
                <SydInput
                  name='intent'
                  disabled={processing}
                  {...(field)}
                />
              </SydLabel>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name='intent'
            control={form.control}
            render={({ field }) => (
              <SydLabel label='What do you want to do?'>
                <SydSelect
                  disabled={processing}
                  name='intent'
                  options={intentOptions}
                  {...(field)}
                />
              </SydLabel>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name='prompt'
            control={form.control}
            rules={{
              required: { value: true, message: 'Please enter a prompt.' }
            }}
            render={({ field, fieldState }) => (
              <SydLabel label='Your prompt' required error={hookFormErrorAdapter(form, fieldState)}>
                <TextareaAutosize
                  disabled={processing}
                  name='intent'
                  style={{ width: '100%' }}
                  minRows={10}
                  {...(field)}
                />
              </SydLabel>
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            name='schedule.interval'
            control={form.control}
            rules={{
            }}
            render={({ field, fieldState }) => (
              <SydLabel label='Schedule Interval' error={hookFormErrorAdapter(form, fieldState)}>
                <SydSelect
                  disabled={processing}
                  name='schedule.interval'
                  options={intervalOptions}
                  {...(field)}
                />
              </SydLabel>
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            name='schedule.bound'
            control={form.control}
            rules={{
            }}
            render={({ field, fieldState }) => (
              <SydLabel label='Start or end of interval?' error={hookFormErrorAdapter(form, fieldState)}>
                <SydSelect
                  disabled={processing}
                  name='schedule.bound'
                  options={boundaryOptions}
                  {...(field)}
                />
              </SydLabel>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <FlexRow className='gap-md justify-end'>
            <SydButton size='lg' variant='ghost' onClick={() => history.go(-1)} disabled={processing}>Cancel</SydButton>
            <SydButton size='lg' variant='primary' onClick={submitter} processing={processing}>Create Schedule</SydButton>
          </FlexRow>
        </Grid>
      </Grid>
    </PageLayout>
  )
}

export default NewSchedule
